import { Option, PlanCardsMetadata, Product } from '~/types/products';
import { hasValidReturnVisitorCookie } from './returnVisitorCookie';
import { UNDERPERFORMING_STATES } from '~/constants/offers';
import { OfferMerchMetadata } from '~/types/offerObject';

export const getOfferBannerText = (
  offerMerchandisingData: OfferMerchMetadata | PlanCardsMetadata,
  product: Product
): string | null => {
  // Handle new array-based structure (OfferMerchMetadata)
  if (
    offerMerchandisingData &&
    'productCards' in offerMerchandisingData &&
    Array.isArray(offerMerchandisingData.productCards)
  ) {
    const productCards = offerMerchandisingData.productCards;
    const matchingProductCard = productCards.find(
      (card) => card.productCardHash === product.dataspeed?.downloadInMbps?.toString()
    );

    if (matchingProductCard?.outerBanner?.copy) {
      return matchingProductCard.outerBanner.copy;
    }

    if (matchingProductCard?.simplified?.banners?.length > 0) {
      return matchingProductCard.simplified?.banners?.[0]?.copy || null;
    }
  }

  // Handle new keyed object structure
  const speedKey = product.dataspeed?.downloadInMbps?.toString();
  if (speedKey && offerMerchandisingData?.[speedKey]) {
    const speedData = offerMerchandisingData[speedKey];

    // Check outerBanner first
    if (speedData.outerBanner?.copy) {
      return speedData.outerBanner.copy;
    }

    // Check simplified banners
    if (speedData.simplified?.banners?.length > 0) {
      return speedData.simplified.banners[0].copy;
    }

    // Fall back to innerBanner
    if (speedData.innerBanner?.copy && speedData.innerBanner?.copy !== 'none') {
      return speedData.innerBanner.copy;
    }
  }

  return null;
};

/**
 * Get RVO (Return Visitor Offer) banner text only
 * Returns null if no RVO banner is available
 */
export const getRvoBannerText = (
  offerMerchandisingData: OfferMerchMetadata | PlanCardsMetadata,
  product: Product
): string | null => {
  if (
    offerMerchandisingData &&
    'returnVisitorOffer' in offerMerchandisingData &&
    offerMerchandisingData.returnVisitorOffer
  ) {
    const currentSpeed = product.dataspeed?.downloadInMbps?.toString();
    const offerData = offerMerchandisingData as OfferMerchMetadata;
    const bannerText =
      offerData.returnVisitorOffer?.speeds?.[
        currentSpeed as keyof typeof offerData.returnVisitorOffer.speeds
      ]?.rvoProductBannerText;

    if (bannerText) {
      return bannerText;
    }
  }

  return null;
};

/**
 * Get offer banner text excluding RVO banners
 * Use this when you need regular offer text separately from RVO
 */
export const getNonRvoOfferBannerText = (
  offerMerchandisingData: OfferMerchMetadata | PlanCardsMetadata,
  product: Product
): string | null => {
  // Handle new array-based structure (OfferMerchMetadata)
  if (
    offerMerchandisingData &&
    'productCards' in offerMerchandisingData &&
    Array.isArray(offerMerchandisingData.productCards)
  ) {
    const productCards = offerMerchandisingData.productCards;
    const matchingProductCard = productCards.find(
      (card) => card.productCardHash === product.dataspeed?.downloadInMbps?.toString()
    );

    if (matchingProductCard?.outerBanner?.copy) {
      return matchingProductCard.outerBanner.copy;
    }
  }

  // Handle new keyed object structure
  const speedKey = product.dataspeed?.downloadInMbps?.toString();
  if (speedKey && offerMerchandisingData?.[speedKey]) {
    const speedData = offerMerchandisingData[speedKey];

    // Check outerBanner first
    if (speedData.outerBanner?.copy) {
      return speedData.outerBanner.copy;
    }

    // Fall back to innerBanner
    if (speedData.innerBanner?.copy && speedData.innerBanner?.copy !== 'none') {
      return speedData.innerBanner.copy;
    }
  }

  return null;
};

export const getVrcBannerText = (
  offerMerchandisingData: OfferMerchMetadata | PlanCardsMetadata,
  product: Product,
  sweetener: Option
): string | null => {
  const dataspeed = product.dataspeed;
  const sweeteners = product.sweeteners;
  const vrcSweetener = sweetener?.value === 'visa';

  if (
    vrcSweetener &&
    sweeteners.vrc &&
    offerMerchandisingData?.[dataspeed?.downloadInMbps?.toString()]?.outerBanner?.copy
  ) {
    const vrcBannerCopy = offerMerchandisingData?.[dataspeed?.downloadInMbps?.toString()]?.outerBanner?.copy;
    return vrcBannerCopy;
  }

  return null;
};

export const getSecondaryOffer = (
  offerMerchandisingData: OfferMerchMetadata | PlanCardsMetadata,
  product: Product
): { bannerText: string } | null => {
  // Handle new array-based structure (OfferMerchMetadata)
  if (
    offerMerchandisingData &&
    'productCards' in offerMerchandisingData &&
    Array.isArray(offerMerchandisingData.productCards)
  ) {
    const matchingProductCard = offerMerchandisingData.productCards.find(
      (card) => card.productCardHash === product.dataspeed?.downloadInMbps?.toString()
    );

    if (matchingProductCard?.innerBanner?.copy) {
      return {
        bannerText: matchingProductCard.innerBanner.copy,
      };
    }
  }

  // Handle new keyed object structure
  const speedKey = product.dataspeed?.downloadInMbps?.toString();
  if (speedKey && offerMerchandisingData?.[speedKey]) {
    const speedData = offerMerchandisingData[speedKey];

    if (speedData.innerBanner?.copy && speedData.innerBanner?.copy !== 'none') {
      return {
        bannerText: speedData.innerBanner.copy,
      };
    }
  }

  return null;
};

/**
 * Check if user is in an underperforming state
 * @param addressState - The user's address state (e.g., 'WI', 'IL', etc.)
 * @returns boolean - Whether the user is in an underperforming state
 */
export const isUnderperformingState = (addressState?: string): boolean => {
  if (!addressState) return false;
  return UNDERPERFORMING_STATES.includes(addressState.toUpperCase() as (typeof UNDERPERFORMING_STATES)[number]);
};

/**
 * Build headers for return visitor offer requests
 * @param returnVisitorOfferEligibility - Whether return visitor offer is eligible
 * @param isCopper - Whether the user is a copper user (copper users cannot have the header set to true)
 * @returns HeadersInit object with return visitor headers if applicable
 */
export const buildReturnVisitorHeaders = (
  returnVisitorOfferEligibility: boolean,
  isCopper?: boolean
): HeadersInit => {
  // Check for `rvoTest` queryParam in the URL to override logic
  let rvoParamActive = false;
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    if (params.get('rvoTest') === 'true') {
      rvoParamActive = true;
    }
  }

  // Copper users cannot have the x-cohort-return-visitor header set to true
  if ((rvoParamActive || (hasValidReturnVisitorCookie() && returnVisitorOfferEligibility)) && !isCopper) {
    return {
      'x-cohort-return-visitor': 'true',
    };
  }

  return {};
};
